import type { VideoSetSource } from "@pax/api-contract/common";
import {
  boundedTime,
  type PlayerOptions,
  type ReportPlayback,
  type VideoPlayback,
} from "./playback";
import { createAudioPlayback } from "./software/audio-playback";
import { decoderThreadCount } from "./software/decoder-threads";
import type { DecodedVideoFrame, DecoderRequest, DecoderResponse } from "./software/messages";
import { createYuvRenderer } from "./software/yuv-renderer";

const startSeconds = 0.2;
const rebufferSeconds = 0.5;
const keyframeImageBytes = 16 * 1024 * 1024;

type FrameMessage = Extract<DecoderResponse, { type: "frame" }>;

/**
 * A looping clip's frames keep counting up past its end, a lap at a time, so the clock and the frame
 * queue run straight through the seam; `mediaTime` folds them back into the clip.
 */
export function createSoftwarePlayer(
  canvas: HTMLCanvasElement,
  source: VideoSetSource,
  { duration, hasAudio, loop, onFrame }: PlayerOptions & { duration: number; hasAudio: boolean },
  report: ReportPlayback,
): VideoPlayback {
  let worker: Worker | null = null;
  let renderer: ReturnType<typeof createYuvRenderer> | null = null;
  let audioContext: AudioContext | null = null;
  let audio: ReturnType<typeof createAudioPlayback> | null = null;
  let gain: GainNode | null = null;
  let volume = 1;
  let muted = false;
  let ready = false;
  let disposed = false;
  let failed = false;
  let playing = false;
  let waiting = false;
  let rebuffering = false;
  let seeking = false;
  let ended = false;
  let hasFrame = false;
  let audioAvailable = hasAudio && !loop;
  let audioRequested = false;
  let downloadRequested = false;
  let frameCapacity = 3;
  let generation = 0;
  let seekInFlight = false;
  let pendingSeek: number | null = null;
  let videoEnded = false;
  let frames: DecodedVideoFrame[] = [];
  let displayed: { timestamp: number; duration: number } | null = null;
  let displayedFromStream = false;
  let keyframes: number[] = [];
  const keyframeImages = new Map<number, DecodedVideoFrame>();
  let keyframeImageBytesUsed = 0;
  let previewingPendingSeek = false;
  let audioGeneration = 0;
  let audioEnded = !audioAvailable;
  let lapSeconds = duration;
  let position = 0;
  let clockStarted = 0;
  let animation = 0;
  let lastReport = 0;

  function send(request: DecoderRequest, transfer: Transferable[] = []) {
    worker?.postMessage(request, transfer);
  }
  function stopWorker() {
    const stopped = worker;
    worker = null;
    if (!stopped) return;
    const timeout = setTimeout(() => stopped.terminate(), 2000);
    stopped.onmessage = event => {
      if (event.data.type !== "disposed") return;
      clearTimeout(timeout);
      stopped.terminate();
    };
    stopped.onerror = () => {
      clearTimeout(timeout);
      stopped.terminate();
    };
    stopped.postMessage({ type: "dispose" } satisfies DecoderRequest);
  }
  function releaseDecoder() {
    stopWorker();
    ready = false;
    frames = [];
    keyframeImages.clear();
    keyframeImageBytesUsed = 0;
    seekInFlight = false;
    pendingSeek = null;
    videoEnded = false;
    audioRequested = false;
    downloadRequested = false;
    audioEnded = !audioAvailable;
    audio?.reset();
    audio = null;
    gain = null;
    const context = audioContext;
    audioContext = null;
    void context?.close().catch(() => undefined);
  }
  function releaseFrames(frameGeneration: number, released: DecodedVideoFrame[]) {
    if (!released.length) return;
    const buffers = released.map(frame => frame.pixels.buffer);
    send({ type: "release-frames", generation: frameGeneration, buffers }, buffers);
  }
  function releasePlayedAudio(time: number) {
    const count = audio?.discardBefore(time) ?? 0;
    if (count) send({ type: "release-audio", generation: audioGeneration, count });
  }
  // The audio context time audible at `now`. Unlike `currentTime`, the output timestamp
  // includes output latency and advances smoothly between audio callbacks.
  function clock(now: number) {
    if (!audioContext) return now / 1000;
    const { contextTime, performanceTime } = audioContext.getOutputTimestamp();
    if (contextTime && performanceTime) return contextTime + (now - performanceTime) / 1000;
    return audioContext.currentTime - audioContext.baseLatency - (audioContext.outputLatency || 0);
  }
  function running() {
    return playing && !waiting && !seeking;
  }
  function currentTime(now = performance.now()) {
    const elapsed = running() ? Math.max(0, clock(now) - clockStarted) : 0;
    return loop ? position + elapsed : boundedTime(position + elapsed, duration);
  }
  function mediaTime(time: number) {
    if (!loop) return time;
    const folded = time % lapSeconds;
    // Laps are added up in floating point, so a lap's first frame can land a hair before the end.
    return lapSeconds - folded < 1e-6 ? 0 : folded;
  }
  function startClock() {
    clockStarted = audioContext ? audioContext.currentTime + 0.02 : clock(performance.now());
    audio?.start(position, clockStarted);
  }
  function holdClock() {
    position = currentTime();
    cancelAnimationFrame(animation);
    audio?.pause();
  }
  function suspendAudio() {
    if (audioContext?.state === "running") void audioContext.suspend().catch(() => undefined);
  }
  function restartAudio() {
    audio?.reset();
    audioGeneration++;
    audioEnded = !audioAvailable;
    if (audioRequested && audioAvailable)
      send({ type: "restart-audio", generation: audioGeneration, seconds: position });
  }
  function audioBufferedUntil() {
    return audio?.bufferedUntil(position) ?? position;
  }
  function fail(message: string) {
    if (disposed || failed) return;
    position = currentTime();
    playing = false;
    waiting = false;
    seeking = false;
    failed = true;
    cancelAnimationFrame(animation);
    releaseDecoder();
    renderer?.dispose();
    renderer = null;
    report({ error: message, waiting: false, seeking: false, playing: false });
  }
  function onContextLost(event: Event) {
    event.preventDefault();
    fail("The video display was interrupted. Try again.");
  }
  canvas.addEventListener("webglcontextlost", onContextLost);
  // Previews show an earlier keyframe; the decoder only continues from frames of its stream.
  function draw(frame: DecodedVideoFrame, fromStream: boolean) {
    renderer ??= createYuvRenderer(canvas);
    renderer.draw(frame);
    onFrame?.(mediaTime(frame.timestamp));
    displayed = { timestamp: frame.timestamp, duration: frame.duration };
    displayedFromStream = fromStream;
    if (!hasFrame) {
      hasFrame = true;
      report({ hasFrame: true });
    }
  }
  function rememberKeyframe(frame: DecodedVideoFrame) {
    if (keyframeImages.has(frame.timestamp)) return;
    const image = { ...frame, pixels: frame.pixels.slice() };
    keyframeImages.set(frame.timestamp, image);
    keyframeImageBytesUsed += image.pixels.byteLength;
    for (const [timestamp, oldest] of keyframeImages) {
      if (keyframeImageBytesUsed <= keyframeImageBytes) break;
      keyframeImages.delete(timestamp);
      keyframeImageBytesUsed -= oldest.pixels.byteLength;
    }
  }
  function keyframeImageAt(time: number) {
    let keyframe: number | undefined;
    for (const timestamp of keyframes) {
      if (timestamp > time) break;
      keyframe = timestamp;
    }
    const image = keyframe === undefined ? undefined : keyframeImages.get(keyframe);
    if (image && keyframe !== undefined) {
      keyframeImages.delete(keyframe);
      keyframeImages.set(keyframe, image);
    }
    return image;
  }
  function covers(frame: { timestamp: number; duration: number }, time: number) {
    return frame.timestamp <= time && time < frame.timestamp + frame.duration;
  }
  function finish() {
    position = duration;
    playing = false;
    waiting = false;
    rebuffering = false;
    ended = true;
    cancelAnimationFrame(animation);
    audio?.reset();
    suspendAudio();
    report({ currentTime: duration, ended: true, playing: false, waiting: false, seeking: false });
  }
  function resume() {
    if (!playing || !waiting || seeking || !ready) return;
    if (audioContext && audioContext.state !== "running") return;
    if (position >= duration && videoEnded) {
      finish();
      return;
    }
    const seconds = rebuffering ? rebufferSeconds : startSeconds;
    const neededFrames = Math.min(frameCapacity, Math.ceil(source.frameRate * seconds));
    if (!videoEnded && frames.length < neededFrames) return;
    if (audioAvailable && !audioEnded && audioBufferedUntil() - position < seconds) return;
    waiting = false;
    rebuffering = false;
    report({ waiting: false });
    startClock();
    cancelAnimationFrame(animation);
    animation = requestAnimationFrame(tick);
  }
  // Frames are chosen with the animation frame's vsync-aligned timestamp, so callback timing
  // jitter cannot flip a frame between one and three vsyncs.
  function tick(frameTime: number) {
    if (!running() || disposed) return;
    const time = currentTime(frameTime);
    const passed: DecodedVideoFrame[] = [];
    while (frames[0] && frames[0].timestamp <= time) {
      const frame = frames.shift();
      if (frame) passed.push(frame);
    }
    const latest = passed.at(-1);
    if (latest) draw(latest, true);
    releaseFrames(generation, passed);
    releasePlayedAudio(time);
    if (time >= duration && videoEnded && !frames.length) {
      finish();
      return;
    }
    const frameEnd = displayed ? displayed.timestamp + displayed.duration : position;
    const videoStarved = !videoEnded && !frames.length && time >= frameEnd;
    const audioStarved = audioAvailable && !audioEnded && time >= audioBufferedUntil();
    if (videoStarved || audioStarved) {
      holdClock();
      position = Math.min(
        time,
        videoStarved ? frameEnd : time,
        audioStarved ? audioBufferedUntil() : time,
      );
      waiting = true;
      rebuffering = true;
      report({ waiting: true, currentTime: mediaTime(position) });
      return;
    }
    const now = performance.now();
    if (now - lastReport >= 100) {
      lastReport = now;
      report({ currentTime: mediaTime(time) });
    }
    animation = requestAnimationFrame(tick);
  }
  function seekDecoder(time: number) {
    const stale = frames;
    frames = [];
    generation++;
    seekInFlight = true;
    seeking = true;
    videoEnded = false;
    // Sent before the stale frames return, so their credits cannot restart the old stream.
    send({ type: "seek", generation, seconds: time });
    releaseFrames(generation - 1, stale);
  }
  function showPosition(time: number) {
    if (!worker) {
      load();
      return;
    }
    if (seekInFlight) {
      // The decoder is behind the pointer: show this position's keyframe if it was decoded before.
      pendingSeek = time;
      seeking = true;
      const image = keyframeImageAt(time);
      previewingPendingSeek = image !== undefined;
      if (image && displayed?.timestamp !== image.timestamp) draw(image, false);
      return;
    }
    if (!seeking && displayedFromStream && displayed && covers(displayed, time)) return;
    const index = seeking ? -1 : frames.findIndex(frame => covers(frame, time));
    if (index < 0) {
      seekDecoder(time);
      return;
    }
    const skipped = frames.splice(0, index + 1);
    const frame = skipped[index];
    if (frame) draw(frame, true);
    releaseFrames(generation, skipped);
  }
  function receiveFrame({ generation: frameGeneration, frame, keyframe, preview }: FrameMessage) {
    if (keyframe && !loop) rememberKeyframe(frame);
    if (frameGeneration !== generation) {
      releaseFrames(frameGeneration, [frame]);
      return;
    }
    seekInFlight = false;
    if (!preview && !seeking) {
      frames.push(frame);
      resume();
      return;
    }
    if (pendingSeek === null || !previewingPendingSeek) draw(frame, !preview);
    releaseFrames(generation, [frame]);
    // After a preview the decoder is still skipping toward the old target, so only the
    // target frame lets later positions reuse what is on screen or queued.
    if (!preview) seeking = false;
    if (pendingSeek !== null) {
      const time = pendingSeek;
      pendingSeek = null;
      previewingPendingSeek = false;
      showPosition(time);
    }
    if (!seeking) report({ seeking: false });
    resume();
  }
  function onMessage(message: DecoderResponse) {
    if (disposed || failed) return;
    if (message.type === "error") fail(message.message);
    else if (message.type === "ready") {
      canvas.dataset.videoThreads = String(message.threads);
      ready = true;
      frameCapacity = message.frameCapacity;
      keyframes = message.keyframes;
      lapSeconds = message.lapSeconds;
      audioAvailable = hasAudio && !loop && message.audioChannels > 0;
      if (!audioAvailable) audioEnded = true;
      report({ duration: message.duration });
      resume();
    } else if (message.type === "frame") receiveFrame(message);
    else if (message.type === "video-ended") {
      if (message.generation !== generation) return;
      videoEnded = true;
      if (seeking && !seekInFlight) {
        seeking = false;
        report({ seeking: false });
      }
      resume();
    } else if (message.type === "audio") {
      if (message.generation !== audioGeneration) return;
      audio?.enqueue(message.chunk);
      resume();
    } else if (message.type === "audio-ended") {
      if (message.generation !== audioGeneration) return;
      audioEnded = true;
      resume();
    }
  }
  function load() {
    if (worker || failed || disposed) return;
    try {
      worker = new Worker(new URL("./software/video-decoder.worker.ts", import.meta.url), {
        type: "module",
      });
      worker.onmessage = event => {
        try {
          onMessage(event.data);
        } catch (error) {
          fail(error instanceof Error ? error.message : "The video could not be displayed.");
        }
      };
      worker.onerror = () => fail("The video decoder could not be started. Try again.");
      send({
        type: "load",
        url: source.url,
        duration,
        frameRate: source.frameRate,
        threads: decoderThreadCount(source.width, source.height),
        loop,
      });
      seekDecoder(position);
    } catch {
      fail("The video decoder could not be started. Try again.");
    }
  }
  function seek(seconds: number) {
    if (disposed || failed) return;
    holdClock();
    position = boundedTime(seconds, duration);
    ended = !playing && position >= duration;
    waiting = playing;
    rebuffering = false;
    restartAudio();
    showPosition(position);
    report({ currentTime: position, seeking, waiting, ended, error: null });
    resume();
  }

  return {
    getCurrentTime: () => mediaTime(currentTime()),
    prepare: load,
    async play() {
      if (disposed) return;
      if (failed) {
        failed = false;
        hasFrame = false;
        displayed = null;
        report({ hasFrame: false });
      }
      playing = true;
      waiting = true;
      rebuffering = false;
      report({ playing: true, waiting: true, ended: false, error: null });
      if (ended || (!loop && position >= duration)) seek(0);
      // Started before the audio context, whose creation can block for a moment.
      load();
      if (!downloadRequested) {
        downloadRequested = true;
        send({ type: "download" });
      }
      if (audioAvailable && !audioContext) {
        audioContext = new AudioContext({ sampleRate: 48000 });
        gain = audioContext.createGain();
        gain.gain.value = muted ? 0 : volume;
        gain.connect(audioContext.destination);
        audio = createAudioPlayback(audioContext, gain);
      }
      if (!audioRequested) {
        audioRequested = true;
        restartAudio();
      }
      const context = audioContext;
      try {
        await context?.resume();
      } catch (error) {
        if (disposed || !playing || context !== audioContext) return;
        throw error;
      }
      if (disposed || !playing || context !== audioContext) {
        if (context === audioContext) suspendAudio();
        return;
      }
      resume();
    },
    pause() {
      if (playing) holdClock();
      playing = false;
      waiting = false;
      rebuffering = false;
      releasePlayedAudio(position);
      suspendAudio();
      report({ playing: false, waiting: false, currentTime: mediaTime(position) });
    },
    seek,
    setVolume(next) {
      volume = next;
      if (gain) gain.gain.value = muted ? 0 : volume;
      report({ volume });
    },
    setMuted(next) {
      muted = next;
      if (gain) gain.gain.value = muted ? 0 : volume;
      report({ muted });
    },
    dispose() {
      if (disposed) return;
      position = currentTime();
      playing = false;
      disposed = true;
      canvas.removeEventListener("webglcontextlost", onContextLost);
      cancelAnimationFrame(animation);
      releaseDecoder();
      renderer?.dispose();
      renderer = null;
      displayed = null;
      canvas.width = 0;
      canvas.height = 0;
    },
  };
}
